// src/components/workspace/session/SessionPanel.js

import { useState } from "react";
import {
  Mic,
  MonitorUp,
  PhoneCall,
  PhoneOff,
  Video as VideoIcon,
  X,
} from "lucide-react";
import SessionPreflight from "./SessionPreflight";

/**
 * Wraps the live call with its own state machine so the ring, decline, and
 * no-answer states are handled here rather than as banners scattered through
 * the workspace page.
 *
 * The call itself stays mounted whenever a session is live; only the
 * surrounding pre-call UI changes.
 */

const CONNECTION_LABELS = {
  connected: { label: "Connected", tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  connecting: { label: "Connecting", tone: "text-amber-700 bg-amber-50 border-amber-200" },
  new: { label: "Connecting", tone: "text-amber-700 bg-amber-50 border-amber-200" },
  reconnecting: { label: "Reconnecting", tone: "text-amber-700 bg-amber-50 border-amber-200" },
  disconnected: { label: "Disconnected", tone: "text-rose-700 bg-rose-50 border-rose-200" },
  failed: { label: "Connection failed", tone: "text-rose-700 bg-rose-50 border-rose-200" },
  closed: { label: "Session closed", tone: "text-slate-600 bg-slate-50 border-slate-200" },
};

function MediaChip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

export default function SessionPanel({
  sessionLive,
  outgoingPhase,
  showOutgoingState,
  incomingFrom,
  otherParticipantName,
  otherPresence = {},
  connectionState,
  onStart,
  onAccept,
  onDecline,
  onCancel,
  children,
}) {
  const connection =
    CONNECTION_LABELS[String(connectionState || "").toLowerCase()] || null;

  // The pre-flight gate sits between the intent to call and the call itself.
  const [checking, setChecking] = useState(false);

  const beginCall = (preflight) => {
    setChecking(false);
    onStart(preflight);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {checking && !sessionLive ? (
        <SessionPreflight
          onReady={beginCall}
          onCancel={() => setChecking(false)}
        />
      ) : sessionLive ? (
        <>
          <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 px-3 py-2">
            {connection && (
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${connection.tone}`}
              >
                {connection.label}
              </span>
            )}
            {otherPresence?.video && <MediaChip icon={VideoIcon} label="Video" />}
            {otherPresence?.audio && <MediaChip icon={Mic} label="Audio" />}
            {otherPresence?.screenSharing && (
              <MediaChip icon={MonitorUp} label="Sharing screen" />
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">{children}</div>
        </>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 p-5 text-center">
          {incomingFrom ? (
            <>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Incoming session
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  {incomingFrom} is calling.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onAccept}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
                >
                  <PhoneCall className="h-4 w-4" />
                  Join
                </button>
                <button
                  type="button"
                  onClick={onDecline}
                  className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-300 transition hover:bg-slate-50"
                >
                  <PhoneOff className="h-4 w-4" />
                  Decline
                </button>
              </div>
            </>
          ) : showOutgoingState && outgoingPhase === "ringing" ? (
            <>
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                Calling {otherParticipantName || "the other participant"}…
              </div>
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-300 transition hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </>
          ) : showOutgoingState && outgoingPhase === "declined" ? (
            <>
              <p className="text-sm font-medium text-slate-700">
                The call was declined.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setChecking(true)}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                >
                  Call again
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-300 transition hover:bg-slate-50"
                >
                  Dismiss
                </button>
              </div>
            </>
          ) : showOutgoingState && outgoingPhase === "missed" ? (
            <>
              <p className="text-sm font-medium text-slate-700">
                No answer from {otherParticipantName || "the other participant"}.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setChecking(true)}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                >
                  Try again
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-300 transition hover:bg-slate-50"
                >
                  Dismiss
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <VideoIcon className="h-5 w-5 text-slate-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  No session running
                </p>
                <p className="mt-1 max-w-xs text-xs leading-relaxed text-slate-500">
                  This session is built around a shared screen, not a talking
                  head. Your setup is checked first so the other party is not
                  called into a connection that cannot work.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setChecking(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                <PhoneCall className="h-4 w-4" />
                Check setup & start
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
